/* =============================================================================
   VIEWS / ADMIN / SETTINGS-PRINTERS — Drucker-CRUD + Routing + Testdruck
   =============================================================================
   Wird als eigene Card im Settings-Tab gerendert. Bearbeitet die Sub-Keys
   `printers[]` und `printerRouting{}` der restaurants.settings-JSONB-Spalte.

   Export:
     renderPrintersCard()           — HTML-Snippet (eine Settings-Card)
     printersAddNew()               — neuen Slot anhängen
     printersEdit(id, field, value) — einzelnes Feld eines Druckers ändern
     printersDelete(id)             — Drucker löschen (mit Bestätigung)
     printersToggleActive(id)       — Aktiv-Toggle
     printersTestPrint(id)          — Testdruck-Button-Handler
     printersUpdateRouting(key, v)  — Routing-Toggle
     openPrinterMockPreview(html)   — Modal mit Mock-Bon-Preview
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import {
  listPrinters, getRouting, testPrint,
  newPrinterSlotId,
  PRINTER_KINDS, PRINTER_BACKENDS
} from '../../core/printer-adapter.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

async function savePrinters(next) {
  if (typeof window.updateSetting !== 'function') {
    throw new Error('updateSetting nicht verfügbar (settings.js noch nicht geladen)');
  }
  await window.updateSetting('printers', next);
}

async function saveRouting(next) {
  if (typeof window.updateSetting !== 'function') return;
  await window.updateSetting('printerRouting', next);
}

// -----------------------------------------------------------------------------
// Card-Renderer
// -----------------------------------------------------------------------------
export function renderPrintersCard() {
  const printers = listPrinters();
  const routing  = getRouting();

  const printerRows = printers.length === 0
    ? `<p class="hint" style="margin: 0 0 12px;">Noch keine Drucker eingerichtet. Klicke unten auf „Drucker hinzufügen", um einen Mock-, Bridge- oder Cloud-Drucker zu konfigurieren.</p>`
    : printers.map(printerRowHtml).join('');

  return `
    <h2>Drucker &amp; Bon-Pipeline</h2>
    <p class="hint" style="margin: 0 0 12px;">
      Hybrid-Adapter: Mock (Preview), Bridge (lokaler WebSocket-Server) oder
      Cloud (CloudPRNT / Epson Connect via Polling). Pro Drucker wählbar ob
      Bon-Drucker (Theke) oder Küchen-Drucker.
    </p>

    ${renderBonPrinterWidthSwitch()}

    <h3 style="margin-top: 18px; font-size: 1rem;">Drucker-Slots</h3>
    <div id="printers-list" class="printers-list">
      ${printerRows}
    </div>

    <div class="btn-row" style="margin-top: 12px;">
      <button class="btn btn-secondary btn-sm" onclick="printersAddNew()">+ Drucker hinzufügen</button>
    </div>

    <h3 style="margin-top: 18px; font-size: 1rem;">Routing</h3>
    <p class="hint" style="margin: 0 0 8px;">
      Wann sollen Drucke automatisch ausgelöst werden? Diese Schalter steuern
      ausschließlich den Hardware-Bondrucker. Das Beleg-Aktion-Modal nach
      «Bezahlt» wird separat in <em>Einstellungen → Regeln → Beleg-Aktion</em>
      ein- oder ausgeschaltet. Wenn beides aktiv ist, läuft der Hardware-Druck
      parallel zum Modal — empfohlen ist nur eines von beiden zu aktivieren.
    </p>
    <div class="toggle-row">
      <div>
        <div class="label">Bon automatisch nach „Bezahlt"</div>
        <div class="desc">Alle aktiven Bon-Drucker drucken den Kassenbeleg automatisch, sobald eine Bestellung bezahlt wird. Sinnvoll für Restaurants mit „immer-drucken"-Workflow; bei manueller Entscheidung pro Bon stattdessen das Beleg-Aktion-Modal nutzen.</div>
      </div>
      <label class="toggle">
        <input type="checkbox" ${routing.autoOnPaid ? 'checked' : ''} onchange="printersUpdateRouting('autoOnPaid', this.checked)">
        <span class="toggle-slider"></span>
      </label>
    </div>
    <div class="toggle-row">
      <div>
        <div class="label">Küchen-Bon bei „Bestätigt"</div>
        <div class="desc">Sobald der Operator eine Bestellung annimmt (Status <code>confirmed</code>).</div>
      </div>
      <label class="toggle">
        <input type="checkbox" ${routing.kitchenOnConfirm ? 'checked' : ''} onchange="printersUpdateRouting('kitchenOnConfirm', this.checked)">
        <span class="toggle-slider"></span>
      </label>
    </div>
    <div class="toggle-row">
      <div>
        <div class="label">Küchen-Bon bei „In Zubereitung"</div>
        <div class="desc">Späterer Auslöser, falls die Küche erst nach Annahme + Vorbereitungs-Start eingreifen soll.</div>
      </div>
      <label class="toggle">
        <input type="checkbox" ${routing.kitchenOnPreparing ? 'checked' : ''} onchange="printersUpdateRouting('kitchenOnPreparing', this.checked)">
        <span class="toggle-slider"></span>
      </label>
    </div>
  `;
}

// -----------------------------------------------------------------------------
// Phase 11b — Bondrucker-Breite-Switch mit Mini-Preview
// -----------------------------------------------------------------------------
/**
 * Globaler Switch zwischen 80mm und 58mm für den Browser-Print (window.print).
 * Mini-Preview zeigt einen Mock-Bon in beiden Breiten nebeneinander. Klick
 * auf den Tile aktiviert die jeweilige Breite und speichert sie in
 * settings.bonPrinterWidth.
 */
function renderBonPrinterWidthSwitch() {
  const cur = String((getState().settings || {}).bonPrinterWidth || '80mm').toLowerCase();
  const w   = cur === '58mm' ? '58mm' : '80mm';

  return `
    <h3 style="margin-top: 4px; font-size: 1rem;">Bondrucker-Breite (Browser-Druck)</h3>
    <p class="hint" style="margin: 0 0 6px;">
      Aktuell aktiv: <strong>${w}</strong>. Gilt für jeden „Kassenbon drucken"-Klick
      im Cockpit und für den Tagesabschluss. Hardware-Drucker mit ESC/POS-Backend
      verwenden weiterhin ihre individuell konfigurierte Papierbreite oben in der
      Drucker-Tabelle.
    </p>
    <div class="bonprint-switch" id="bonprint-switch-wrap">
      ${renderBonPrintTile('80mm', w === '80mm')}
      ${renderBonPrintTile('58mm', w === '58mm')}
    </div>
  `;
}

function renderBonPrintTile(width, active) {
  const label = width === '58mm' ? '58 mm (32 Zeichen / Zeile)' : '80 mm (42 Zeichen / Zeile)';
  const hint  = width === '58mm'
    ? 'Klassische schmale Bondrucker (z.B. Epson TM-T20III-58, Star TSP143-Eco)'
    : 'Empfohlen: Star mC-Print3, Epson TM-m30III, Bixolon SRP-350';
  return `
    <div class="bonprint-tile ${active ? 'active' : ''}"
         onclick="setBonPrinterWidth('${width}')">
      <div class="bonprint-tile-label">${label}</div>
      ${renderBonPrintPreview(width)}
      <div class="bonprint-tile-hint">${hint}</div>
    </div>
  `;
}

/**
 * Erzeugt eine winzige Mock-Bon-Vorschau in der gewählten Breite.
 * Monospace, fester Pseudo-Inhalt, gleicher Aufbau wie der echte Bon
 * (Brand, Adresse, Items, Total, MwSt-Split, TSE-Block, Footer).
 */
function renderBonPrintPreview(width) {
  const s = getState().settings || {};
  const brand = htmlEscape((s.restaurantName || 'GRILL & GREEN').toUpperCase());
  const r = s.restaurant || {};
  const addr = htmlEscape([r.street || 'Musterstr. 12', `${r.zip || '12345'} ${r.city || 'Musterstadt'}`].join(' · '));
  const ust  = htmlEscape(r.ustId || 'DE123456789');

  // Item-Beispiel, dass auf 32 Zeichen Zeilenbreite Wort-umgebrochen wird (58mm).
  // Bei 80mm passt es einzeilig.
  const items58 = `
1x Klassik Burger     7,90
1x Pommes klein       3,20
1x Cola 0,3L          2,90`;
  const items80 = `
1x Klassik Burger             7,90
1x Pommes klein               3,20
1x Cola 0,3L                  2,90`;

  const items = width === '58mm' ? items58 : items80;
  const cls   = width === '58mm' ? 'preview-58' : 'preview-80';
  const sep58 = '────────────────────────────';
  const sep80 = '──────────────────────────────────';
  const sep   = width === '58mm' ? sep58 : sep80;

  return `
    <div class="bonprint-preview ${cls}" aria-label="Mini-Bon-Vorschau ${width}">
<span class="bp-brand">${brand}</span>
${addr}
USt-IdNr: ${ust}
${sep}
${items}
${sep}
Summe        13,80 EUR
MwSt 19%      0,93 EUR
${sep}
TSE: Mock · Sig #42
${sep}
Vielen Dank! </div>
  `;
}

/**
 * Klick-Handler: schreibt die neue Breite in settings.bonPrinterWidth.
 * Settings-Re-Render passiert über das updateSetting-Pipeline.
 */
export async function setBonPrinterWidth(width) {
  const next = width === '58mm' ? '58mm' : '80mm';
  if (typeof window.updateSetting !== 'function') return;
  await window.updateSetting('bonPrinterWidth', next);
  rerenderPrintersList();
}

function printerRowHtml(p) {
  const id      = String(p.id || '');
  const name    = String(p.name || '');
  const kind    = String(p.kind || 'receipt');
  const backend = String(p.backend || 'mock');
  const url     = String(p.endpointUrl || '');
  const tok     = String(p.authToken || '');
  const dev     = String(p.deviceId || '');
  const width   = Number(p.paperWidthMm || 80);
  const active  = !!p.isActive;
  const placeholder = (PRINTER_BACKENDS.find(b => b.id === backend) || {}).placeholder || '';

  return `
    <div class="printer-row" data-id="${htmlEscape(id)}" style="border:1px solid var(--border); border-radius:8px; padding:12px; margin-bottom:10px; background: ${active ? 'var(--green-soft)' : 'var(--bg-elev)'};">
      <div style="display:flex; gap:10px; align-items:flex-start;">
        <div style="flex: 1 1 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
          <div class="field">
            <label>Name</label>
            <input type="text" value="${htmlEscape(name)}" placeholder="z.B. Theke / Küche"
                   oninput="printersEdit('${htmlEscape(id)}', 'name', this.value)">
          </div>
          <div class="field">
            <label>Typ</label>
            <select onchange="printersEdit('${htmlEscape(id)}', 'kind', this.value)">
              ${PRINTER_KINDS.map(k => `<option value="${k.id}" ${k.id === kind ? 'selected' : ''}>${k.label}</option>`).join('')}
            </select>
          </div>
          <div class="field">
            <label>Backend</label>
            <select onchange="printersEdit('${htmlEscape(id)}', 'backend', this.value)">
              ${PRINTER_BACKENDS.map(b => `<option value="${b.id}" ${b.id === backend ? 'selected' : ''}>${b.label}</option>`).join('')}
            </select>
          </div>
          <div class="field">
            <label>${backend === 'cloud' ? 'Device-ID (CloudPRNT-Serial)' : 'Endpoint-URL'}</label>
            <input type="text"
                   value="${htmlEscape(backend === 'cloud' ? dev : url)}"
                   placeholder="${htmlEscape(placeholder)}"
                   ${backend === 'mock' ? 'disabled' : ''}
                   oninput="printersEdit('${htmlEscape(id)}', '${backend === 'cloud' ? 'deviceId' : 'endpointUrl'}', this.value)">
          </div>
          <div class="field">
            <label>Auth-Token (optional)</label>
            <input type="text" value="${htmlEscape(tok)}" placeholder="leer lassen, wenn Endpoint ohne Auth"
                   ${backend === 'mock' ? 'disabled' : ''}
                   oninput="printersEdit('${htmlEscape(id)}', 'authToken', this.value)">
          </div>
          <div class="field">
            <label>Papierbreite</label>
            <select onchange="printersEdit('${htmlEscape(id)}', 'paperWidthMm', parseInt(this.value,10))">
              <option value="80" ${width === 80 ? 'selected' : ''}>80 mm (42 Zeichen)</option>
              <option value="58" ${width === 58 ? 'selected' : ''}>58 mm (32 Zeichen)</option>
            </select>
          </div>
        </div>
        <div style="display:flex; flex-direction:column; gap:8px; min-width: 140px;">
          <label class="toggle">
            <input type="checkbox" ${active ? 'checked' : ''} onchange="printersToggleActive('${htmlEscape(id)}')">
            <span class="toggle-slider"></span>
            <span style="margin-left:6px;">${active ? 'Aktiv' : 'Inaktiv'}</span>
          </label>
          <button class="btn btn-secondary btn-sm" onclick="printersTestPrint('${htmlEscape(id)}')">🖨️ Testdruck</button>
          <button class="btn btn-danger btn-sm" onclick="printersDelete('${htmlEscape(id)}')">Löschen</button>
        </div>
      </div>
    </div>
  `;
}

// -----------------------------------------------------------------------------
// CRUD-Handler
// -----------------------------------------------------------------------------
export async function printersAddNew() {
  const next = listPrinters().slice();
  const id = newPrinterSlotId();
  next.push({
    id,
    name:         next.length === 0 ? 'Theke' : `Drucker ${next.length + 1}`,
    kind:         next.length === 1 ? 'kitchen' : 'receipt',
    backend:      'mock',
    endpointUrl:  '',
    authToken:    '',
    deviceId:     '',
    paperWidthMm: 80,
    isActive:     true
  });
  await savePrinters(next);
  rerenderPrintersList();
}

export async function printersEdit(id, field, value) {
  const cur = listPrinters().slice();
  const idx = cur.findIndex(p => p.id === id);
  if (idx === -1) return;
  cur[idx] = { ...cur[idx], [field]: value };
  await savePrinters(cur);
  // Bei backend-Wechsel rerendern, weil das URL/Device-Feld anders heißt.
  if (field === 'backend') rerenderPrintersList();
}

export async function printersDelete(id) {
  if (!(await window.styledConfirm({ title: 'Diesen Drucker entfernen?', danger: true, confirmLabel: 'Entfernen' })).confirmed) return;
  const next = listPrinters().filter(p => p.id !== id);
  await savePrinters(next);
  rerenderPrintersList();
}

export async function printersToggleActive(id) {
  const cur = listPrinters().slice();
  const idx = cur.findIndex(p => p.id === id);
  if (idx === -1) return;
  cur[idx] = { ...cur[idx], isActive: !cur[idx].isActive };
  await savePrinters(cur);
  rerenderPrintersList();
}

export async function printersUpdateRouting(key, value) {
  const cur = getRouting();
  const next = { ...cur, [key]: !!value };
  await saveRouting(next);
}

export async function printersTestPrint(id) {
  showToast('Testdruck wird gesendet…', 'info');
  const res = await testPrint(id);
  if (res && res.ok) {
    showToast('Testdruck OK', 'success');
  }
  // Bei Mock-Backend rendert orders-notifications den Toast schon — hier kein
  // doppelter Erfolgs-Toast nötig. Falls Fehler, testPrint zeigt das schon.
}

// -----------------------------------------------------------------------------
// Re-Render der Liste, ohne den gesamten Settings-Tab neu zu bauen
// -----------------------------------------------------------------------------
function rerenderPrintersList() {
  // Wir rendern die komplette Card neu, damit auch Routing-Toggles aktualisiert
  // werden. Bei Inline-Edits ohne backend-Wechsel reicht's eigentlich, die
  // Zeile zu ersetzen — aber pragmatisch ist der Full-Card-Replace robuster.
  const card = document.getElementById('printers-settings-card');
  if (!card) return;
  card.innerHTML = renderPrintersCard();
}

// -----------------------------------------------------------------------------
// Mock-Preview-Modal: wird via App.events.on('printer:mock-print', …) befüllt
// -----------------------------------------------------------------------------
export function openPrinterMockPreview(payload) {
  if (!payload || !payload.html) return;
  // Doppelten Listener vermeiden — wir nutzen nur ein modal-Overlay.
  const existing = document.getElementById('printer-mock-preview-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'printer-mock-preview-modal';
  modal.className = 'modal-backdrop';
  modal.style.cssText = 'position:fixed; inset:0; background: rgba(20,30,20,.55); backdrop-filter: blur(6px); z-index: 10001; display:flex; align-items:center; justify-content:center; padding: 20px;';
  // S63 Hotfix — z-index 10001, damit Mock-Preview über dem Korrektur-Bon-Modal
  // (z-index 9999) sichtbar wird. Sonst bleibt das Bon-Modal oben und der User
  // sieht nur ein leeres "🖨️ Theke"-Mini-Modal im Hintergrund.
  const kindLabel = ({ receipt: 'Kassenbeleg', kitchen: 'Küchen-Bon', test: 'Testdruck', correction: 'Stornobeleg' })[payload.kind] || payload.kind;
  modal.innerHTML = `
    <div class="modal-card" style="background:var(--bg-elev); border-radius:12px; max-width: 480px; width: 100%; padding: 18px; max-height: 90vh; overflow:auto; display:flex; flex-direction:column; gap:12px;">
      <div style="display:flex; justify-content:space-between; align-items:center;">
        <h3 style="margin:0; font-size: 1.05rem;">🖨️ Druck-Vorschau · ${htmlEscape(payload.printerName || 'Mock-Drucker')}</h3>
        <button class="btn btn-ghost btn-sm" onclick="closePrinterMockPreview()" title="ESC oder Klick außerhalb">✕</button>
      </div>
      <div style="font-size:.82rem; color: var(--ink-soft, #666);">
        ${htmlEscape(kindLabel)} · ${(payload.base64 || '').length} Bytes ESC/POS · Mock-Backend (kein physischer Druck)
      </div>
      <div id="printer-mock-preview-body" style="flex:1; overflow:auto;">
        ${payload.html || '<p style="color:var(--danger);">⚠️ Kein HTML-Render verfügbar.</p>'}
      </div>
      <div style="display:flex; gap:8px; justify-content:flex-end; padding-top:8px; border-top: 1px solid var(--border);">
        <button class="btn btn-ghost" onclick="closePrinterMockPreview()">Schließen</button>
        <button class="btn btn-primary" onclick="printPrinterMockPreview()" title="Browser-Druck der Vorschau">🖨️ Drucken</button>
      </div>
    </div>
  `;
  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.remove();
  });
  // ESC-Taste schließt Modal
  const escHandler = (e) => {
    if (e.key === 'Escape') {
      modal.remove();
      document.removeEventListener('keydown', escHandler);
    }
  };
  document.addEventListener('keydown', escHandler);
  document.body.appendChild(modal);
}

/**
 * S242-HF — Druck-Vorschau schließen bzw. drucken.
 *
 * Beide Buttons standen als Inline-DOM-Ausdruck im Markup
 * (`document.getElementById(…).remove()` bzw. eine ganze IIFE mit
 * `window.open`). Der dom-bindings-Parser löst ausschließlich reine
 * Funktions-Namen auf — beide waren tot, gefunden beim HF-13-Sweep über alle
 * Inline-Handler. Anders als beim Korrekturbeleg-Modal war hier niemand
 * gefangen: ESC und Backdrop-Klick funktionieren.
 */
export function closePrinterMockPreview() {
  const m = document.getElementById('printer-mock-preview-modal');
  if (m) m.remove();
}

export function printPrinterMockPreview() {
  const body = document.getElementById('printer-mock-preview-body');
  if (!body) return;
  const w = window.open('', 'printpreview', 'width=420,height=700');
  if (!w) return;   // Popup-Blocker
  w.document.write(
    '<html><head><title>Bon-Druck</title></head>'
    + '<body style="margin:0;padding:0;">' + body.innerHTML + '</body></html>'
  );
  w.document.close();
  w.focus();
  w.print();
}

// -----------------------------------------------------------------------------
// Mock-Event-Listener registrieren (einmalig beim Modul-Load)
// -----------------------------------------------------------------------------
function ensureMockListener() {
  if (window.__printerMockListenerInstalled) return;
  if (!(window.App && window.App.events && typeof window.App.events.on === 'function')) return;
  window.App.events.on('printer:mock-print', (payload) => {
    openPrinterMockPreview(payload);
  });
  window.__printerMockListenerInstalled = true;
}

// Beim ersten Render der Card setzen wir den Listener auf — falls bootstrap()
// schon gelaufen ist. Falls nicht, wird beim Settings-Tab-Render erneut versucht.
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderPrintersCard:  () => { ensureMockListener(); return renderPrintersCard(); },
    printersAddNew,
    printersEdit,
    printersDelete,
    printersToggleActive,
    printersUpdateRouting,
    printersTestPrint,
    openPrinterMockPreview,
    closePrinterMockPreview,
    printPrinterMockPreview,
    setBonPrinterWidth
  });
  // Versuch direkt beim Modul-Load (falls events.js schon da ist)
  setTimeout(ensureMockListener, 200);
}
